import React from 'react';
import { Grid, Paper, Typography, Box, Tooltip, List, ListItem, ListItemIcon, ListItemText, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import StarIcon from '@material-ui/icons/Star';
import PerformanceDoughnut from './PerformanceDoughnut';
import HorizontalBar from '../../../components/Charts/HorizontalBar';
import { useHistory } from 'react-router-dom';
import Table from '../../../theme/Table';
import Rating from '@material-ui/lab/Rating';
import Select from '../../../theme/CustomMUI/Select';
import vayzLogo from '../../../assets/images/vayz.png';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
    boxShadow: '0 3px 10px rgb(0 0 0 / 0.1)',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  analysisIcon: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
    marginRight: theme.spacing(1)
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    height: 200
  },
  feedbackItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: 20
    }
  },
  positiveIcon: {
    color: '#4caf50'
  },
  negativeIcon: {
    color: '#f44336'
  },
  feedbackText: {
    fontSize: '0.9rem',
    color: '#666'
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: theme.spacing(2),
    textAlign: 'center',
    minHeight: 0
  },
  ratingValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#1976d2',
    textAlign: 'center'
  },
  customTooltip: {
    backgroundColor: 'white',
    color: 'black',
    padding: '10px',
    borderRadius: '10px',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
    maxWidth: '200px',
    '& .MuiTooltip-tooltip': {
      backgroundColor: 'white',
      color: 'black',
      padding: '10px',
      borderRadius: '10px',
      boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
      maxWidth: '200px',
    }
  },
  campaignCard: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0
    }
  }
}));

const PerformanceReport = ({ data, analysisRecords, selectedRecordId, onAnalysisChange }) => {
  const classes = useStyles();
  const history = useHistory();

  const handleRowClick = (staffId) => {
    history.push(`/settings/employees/detail/${staffId}`);
  };

  // Personel performans tablosu için veri hazırlama
  const staffTableData = data?.performance_report?.staff_analysis?.map(staff => ({
    staff_id: staff.staff_id,
    full_name: staff.full_name,
    score: staff.performance.score,
    category: staff.performance.category === 'top_performer' ? 'Üst Düzey' : 'Ortalama',
    strengths: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.strengths?.map((strength, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {strength}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.strengths?.[0] || 'Güçlü Yön bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    ),
    weaknesses: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.weaknesses?.map((weakness, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {weakness}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.weaknesses?.[0] || 'Zayıflık bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    ),
    development_plan: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.development_plan?.map((plan, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {plan}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.development_plan?.[0] || 'Gelişim planı bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    )
  })) || [];

  const tableHeadings = {
    staff_id: { label: "Personel ID" },
    full_name: { label: "İsim Soyisim" },
    score: { label: "Performans Puanı" },
    category: { label: "Kategori" },
    strengths: { label: "Güçlü Yönler" },
    weaknesses: { label: "Zayıflıklar" },
    development_plan: { label: "Gelişim Planı" }
  };

  return (
    <Box>
      {/* Özet Bölümü */}
      <Paper className={classes.paper} style={{ marginBottom: 24 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h5" className={classes.title}>
              <img src={vayzLogo} alt="Vayz Logo" className={classes.analysisIcon} />
              Vayz'ın Genel Değerlendirmesi
            </Typography>
            <Typography variant="body1">
              {data?.summary}
            </Typography>
          </Box>
          <Box width="300px" ml={2}>
            <Select
              label="Analiz Tarihi"
              items={analysisRecords}
              selected={selectedRecordId}
              labelKey="label"
              className={"mt-1"}
              valueKey="value"
              handler={onAnalysisChange}
            />
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={4}>
        {/* Genel Performans Puanı */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" align="center" gutterBottom>
              Genel Performans Puanı
            </Typography>
            <div className={classes.chartContainer}>
              <PerformanceDoughnut
                data={{
                  datasets: [{
                    data: [
                      parseFloat(data?.performance_report?.overall_performance_score) || 0,
                      100 - (parseFloat(data?.performance_report?.overall_performance_score) || 0)
                    ],
                    backgroundColor: ['#1976d2', '#e0e0e0']
                  }]
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%"
                }}
              />
            </div>
            <Typography variant="h4" align="center" color="primary">
              {data?.performance_report?.overall_performance_score}
            </Typography>
          </Paper>
        </Grid>

        {/* En İyi Hizmetler */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              En İyi Hizmetler
            </Typography>
            <div style={{ marginTop: 20 }}>
              {data?.service_analysis?.top_services?.map((service, index) => (
                <HorizontalBar
                  key={service.service_id}
                  title={service.service_name}
                  width={`${95 - (index * 5)}%`}
                  background="rgba(25, 118, 210, 0.8)"
                  label={`${95 - (index * 5)}%`}
                />
              ))}
            </div>
          </Paper>
        </Grid>

        {/* Kampanya Önerileri */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Kampanya Önerileri
            </Typography>
            <div>
              {data?.campaign_suggestions?.map((campaign, index) => (
                <Paper key={index} className={classes.campaignCard} style={{ padding: 16 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {campaign.campaign_name}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {campaign.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Hedef Kitle: {campaign.target_audience}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Gerekçe: {campaign.data_driven_rationale}
                  </Typography>
                </Paper>
              ))}
            </div>
          </Paper>
        </Grid>

        {/* Müşteri Memnuniyeti */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" align="center" gutterBottom>
              Müşteri Memnuniyeti
            </Typography>
            <div className={classes.ratingContainer}>
              <Typography className={classes.ratingValue}>
                {data?.customer_feedback?.feedback_analysis?.sentiment_score ?
                  (parseFloat(data.customer_feedback.feedback_analysis.sentiment_score) / 2).toFixed(1) : '0.0'}/5
              </Typography>
              <Rating
                value={data?.customer_feedback?.feedback_analysis?.sentiment_score ?
                  parseFloat(data.customer_feedback.feedback_analysis.sentiment_score) / 2 : 0}
                precision={0.5}
                readOnly
                size="large"
                max={5}
                style={{ display: 'flex', justifyContent: 'center' }}
              />
            </div>
          </Paper>
        </Grid>

        {/* Olumlu Temalar */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Olumlu Yönler
            </Typography>
            <List>
              {data?.customer_feedback?.feedback_analysis?.positive_themes?.map((theme, index) => (
                <ListItem key={index} className={classes.feedbackItem}>
                  <ListItemIcon>
                    <CheckCircleIcon className={classes.positiveIcon} />
                  </ListItemIcon>
                  <ListItemText primary={theme} className={classes.feedbackText} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Olumsuz Temalar */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Olumsuz Yönler
            </Typography>
            <List>
              {data?.customer_feedback?.feedback_analysis?.negative_themes?.map((theme, index) => (
                <ListItem key={index} className={classes.feedbackItem}>
                  <ListItemIcon>
                    <CancelIcon className={classes.negativeIcon} />
                  </ListItemIcon>
                  <ListItemText primary={theme} className={classes.feedbackText} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Personel Performans Tablosu */}
        <Grid item xs={12} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Personel Performans Analizi
            </Typography>
            <Table
              headings={tableHeadings}
              rows={staffTableData}
              loaded={true}
              onRowClick={handleRowClick}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PerformanceReport; 